/**
 * Semantic Motion Tokens
 *
 * Animation durations, delays, springs and staggers that establish a
 * consistent motion language. Durations follow the rule: UI animations
 * < 300ms. Easing curves are the one platform-specific axis and live in
 * motion.native.ts / motion.web.ts under the same names.
 *
 * Named so no platform resolver can shadow it: `./motion` would resolve to
 * motion.native.ts on React Native, and the platform file would import itself.
 *
 * Usage:
 * - duration.standard: Most state transitions (200ms)
 */

export const duration = {
  /** 100ms — Press feedback, scale responses */
  instant: 100,

  /** 150ms — Tooltips, small popovers */
  quick: 150,

  /** 160ms — Content entering after an in-place selection change */
  contentEnter: 160,

  /** 120ms — Content exiting before an in-place selection change */
  contentExit: 120,

  /** 200ms — Standard transitions, state changes */
  standard: 200,

  /** 300ms — Modals, drawers, dropdowns */
  moderate: 300,

  /** 500ms — Major layout changes, collapsibles */
  slow: 500,

  /** 700ms — One phase of a calm, repeating status pulse */
  statusPulse: 700,

  /** 1000ms — Shimmer loops, background animations */
  glacial: 1000,
} as const;

export const delay = {
  /** 120ms — Initial content reveal after a screen transition starts */
  initialReveal: 120,

  /** 200ms — Follow-on content reveal in an entrance sequence */
  followingReveal: 200,
} as const;

export type Duration = typeof duration;
export type DurationToken = keyof Duration;
export type Delay = typeof delay;
export type DelayToken = keyof Delay;

/**
 * Spring presets — react-native-reanimated / moti on the app, framer-motion / react-spring on the web
 *
 * Snappy: button presses, toggles (~150ms settle)
 * Responsive: form reveals, card entries (~250ms settle)
 * Gentle: page transitions, modals (~350ms settle)
 * Bouncy: celebrations, success states (~400ms with overshoot)
 */
export const spring = {
  snappy: { damping: 15, stiffness: 400 },
  responsive: { damping: 20, stiffness: 300 },
  gentle: { damping: 25, stiffness: 200 },
  bouncy: { damping: 10, stiffness: 250 },
} as const;

/** Stagger delay between items in cascading animations (ms) */
export const stagger = {
  /** 40ms — Tight stagger for small items (OTP cells, dots) */
  tight: 40,
  /** 60ms — Standard stagger for list items, form fields */
  standard: 60,
  /** 80ms — Relaxed stagger for larger elements */
  relaxed: 80,
} as const;

export type Stagger = typeof stagger;
export type StaggerToken = keyof Stagger;

export type Spring = typeof spring;

/** The easing curves every platform must provide, so the two sets cannot drift apart. */
export const EASING_TOKENS = [
  "standard",
  "emphasized",
  "linear",
  "decelerate",
  "accelerate",
] as const;

export type MotionEasingToken = (typeof EASING_TOKENS)[number];
